import { AuthService } from './shared/services/auth/auth.service';
import { urlUtil } from './shared/util';
import _ from 'underscore';
import { StateService, Transition, StateDeclaration, StateParams, TransitionOptions } from '@uirouter/angular';
import {
  STATE_BPPROFILES_ACCESS_SETTINGS,
  STATE_BPPROFILES_VENDOR_ACCREDITATIONS,
  STATE_BPPROFILES_ACSLINFO, STATE_BPPROFILES_BANK, STATE_BPPROFILES_CATALOG, STATE_BPPROFILES_CAVEATS,
  STATE_BPPROFILES_CHECKLISTS, STATE_BPPROFILES_CONTACTS, STATE_BPPROFILES_CONTRACT, STATE_BPPROFILES_DOCUMENTS,
  STATE_BPPROFILES_GENERAL,
  STATE_BPPROFILES_LBDM_CHECKLIST, STATE_BPPROFILES_LOCATIONS, STATE_BPPROFILES_MAILS,
  STATE_BPPROFILES_OEM_OVERVIEW, STATE_BPPROFILES_SLA, STATE_BPPROFILES_TC, STATE_BPPROFILES_TERRITORIES, STATE_BPPROFILES_TICKETS,
  STATE_FAQ,
  STATE_FAQS,
  STATE_GLOBALPRODUCT_EDIT_GENERAL,
  STATE_HOME_PAGE,
  STATE_ORDERENTRY_WEBSHOP,
  STATE_REPORT,
  STATE_USER_INFO,
  STATE_WEBSHOPINFO,
  STATE_WEBSHOPSETUP
} from './shared/app-states';
import { GA1MenuService } from './shared/ga1/ga1-menu.service';
import { Injectable } from '@angular/core';
import { IObject } from '@type_definitions/datatypes';
import { ReportParameterCode, ReportParameterShortCode } from '@app/main/report/report-state.util';

const GA1_URL_REPORT:string = 'ReportView_Edit.jsp';
const GA1_URL_GLOBALPRODUCT:string = 'ProductView_Edit.jsp';
const GA1_URL_USERPROFILE:string = 'UsrRegView_Edit.jsp';
const GA1_URL_ORDERENTRY:string = 'O_OrderView_Edit.jsp';
const GA1_URL_BPPROFILE:string = 'P_SummaryView_Edit';
const GA1_URL_LOGIN:string = 'AM_HomeView_Edit.jsp';
const GA1_URL_RENEW:string = 'AM_HomeView_Renew.jsp';
const GA1_URL_FAQ:string = 'FAQPageView_Edit.jsp';
const GA1_URL_WEBSHOPINFO:string = 'WP_ReqInfoView_Edit.jsp';
const GA1_URL_WEBSHOPWIZARD:string = 'WP_Wizard.jsp';

const stateRedirectOptions:TransitionOptions = {
  location: 'replace',
  inherit: false
};

@Injectable()
export class AppStatesService {
  private readonly _clientRedirects:Function[];
  
  constructor(
    private authService:AuthService,
    private stateService:StateService,
    private ga1MenuService:GA1MenuService
  ) {
    this._clientRedirects = [
      this.reportStateRedirect,
      this.globalProductRedirect,
      this.userProfileRedirect,
      this.orderEntryRedirect,
      this.bpProfileRedirect,
      this.faqRedirect,
      this.webshopInfoRedirect
    ];
  }

  public get clientRedirects():Function[] {
    return this._clientRedirects;
  }

  private reportStateRedirect = (transition:Transition):void => {
    // when the current user has setting 'useGA2Reports', we rewrite old GA1 report links to GA2
    // unless it is explicitely asked (via a url param)
    const toState:StateDeclaration = transition.to();
    const url:string = transition.params('to')?.url;
    
    if (toState.name === STATE_HOME_PAGE
      && url && url.indexOf(GA1_URL_REPORT) >= 0
      && url.indexOf('useGA2Reports=false') === -1) {

      console.debug('Go to GA2 reports');

      const ga1Params:any = urlUtil.getQueryParams(url);
      const ga2Params:any = {
        reportId: ga1Params.row0_Derepid
      };

      if (ga1Params.wv_hideFilters !== '1') {
        ga2Params[ReportParameterShortCode.SHOW_FILTERS] = true;
      }
      if (ga1Params.wv_autoShow === '1') {
        ga2Params[ReportParameterShortCode.AUTO_LOAD] = true;
      }
      if (ga1Params.wv_Detitle) {
        ga2Params[ReportParameterShortCode.TITLE] = ga1Params.wv_Detitle;
      }
      if (ga1Params.wv_Demenid) {
        ga2Params[ReportParameterCode.MENU_ID] = ga1Params.wv_Demenid;
      }

      ga2Params[ReportParameterShortCode.FILTERS] = [];

      // convert GA1 filters
      const skipParameters = [
        'wv_hideFilters',
        'wv_autoShow',
        'wv_Deappid',
        'wv_Demenid',
        'wv_Detitle',
        'wv_show_Btactio'
      ];
      _.forEach(ga1Params, (value:string, name:string) => {
        if (name.indexOf('wv_') === 0 && !_.contains(skipParameters, name)) {
          name = name.replace('wv_', '').replace('_from', '_FROM').replace('_to', '_TO');
          ga2Params[ReportParameterShortCode.FILTERS].push(name + '\t' + value);
        }
      });
      this.stateService.go(STATE_REPORT, ga2Params, stateRedirectOptions);
    }
  };

  private orderEntryRedirect = (transition:Transition):void => {
    const useGA2OrderEntry = this.authService.getCurrentUser() && this.authService.getCurrentUser().useGA2Beta;
    const toState:StateDeclaration = transition.to();
    const url:string = transition.params('to')?.url;
    
    // when the current user has setting 'useGA2Reports', we rewrite old GA1 report links to GA2
    // unless it is explicitely asked (via a url param)
    if (useGA2OrderEntry
      && toState.name === STATE_HOME_PAGE
      && url && url.indexOf(GA1_URL_ORDERENTRY) >= 0
      && url.indexOf('ejbEvent=create') >= 0) {

      console.debug('Go to GA2 order entry');
      this.stateService.go(STATE_ORDERENTRY_WEBSHOP, { new: true }, stateRedirectOptions);
    }
  };

  private globalProductRedirect = (transition:Transition):void => {
    const toState:StateDeclaration = transition.to();
    const url:string = transition.params('to')?.url;
    
    if (toState.name === STATE_HOME_PAGE
      && url && url.indexOf(GA1_URL_GLOBALPRODUCT) >= 0) {

      console.debug('Go to GA2 global products');

      const ga1Params:any = urlUtil.getQueryParams(url);
      const ga2Params:any = {
        product: ga1Params.row0_Deprdid
      };
      this.stateService.go(STATE_GLOBALPRODUCT_EDIT_GENERAL, ga2Params, stateRedirectOptions);
    }
  };

  private userProfileRedirect = (transition:Transition):void => {
    const toState:StateDeclaration = transition.to();
    const url:string = transition.params('to')?.url;
    
    if (toState.name === STATE_HOME_PAGE
      && url && url.indexOf(GA1_URL_USERPROFILE) >= 0) {

      console.debug('Go to GA2 user profiles');

      const ga1Params:any = urlUtil.getQueryParams(url);
      const ga2Params:any = {
        userId: ga1Params.ejbEvent === 'create'? 'NEW' : ga1Params.row0_Deconid
      };
      this.stateService.go(STATE_USER_INFO, ga2Params, stateRedirectOptions);
    }
  };

  private bpProfileRedirect = (transition:Transition):void => {
    //https://localhost:8443/ga2/#/unacc?url=%2Funacc%2FP_SummaryView_Edit.jsp%3Frow0_Deccoid%3D54550
    const toState:StateDeclaration = transition.to();
    const url:string = transition.params('to')?.url;
    
    if (toState.name === STATE_HOME_PAGE
      && url && url.indexOf(GA1_URL_BPPROFILE) >= 0) {

      console.debug('Go to GA2 BP profiles');

      const ga1Params:any = urlUtil.getQueryParams(url);
      const ga2Params:any = {
        profileListType: 'bpprofiles',
        profileId: ga1Params.row0_Deproid || `cc:${ga1Params.row0_Deccoid}`
      };
      let ga2State:string;

      switch (ga1Params.wv_showTab) {
        case 'Checklist':
          ga2State = STATE_BPPROFILES_CHECKLISTS;
          break;
        case 'LBDM':
          ga2State = STATE_BPPROFILES_LBDM_CHECKLIST;
          break;
        case 'BankLoc':
        case 'BankInt':
        case 'BankEUR':
          ga2State = STATE_BPPROFILES_BANK;
          break;
        case 'Access':
          ga2State = STATE_BPPROFILES_ACCESS_SETTINGS;
          break;
        case 'Roles':
          ga2State = STATE_BPPROFILES_CONTACTS;
          break;
        case 'Locations':
          ga2State = STATE_BPPROFILES_LOCATIONS;
          break;
        case 'DRSLAVA':
          ga2State = STATE_BPPROFILES_SLA;
          break;
        case 'DRPACON':
          ga2State = STATE_BPPROFILES_TC;
          break;
        case 'CAVEATS':
          ga2State = STATE_BPPROFILES_CAVEATS;
          break;
        case 'DRVENPA':
          ga2State = STATE_BPPROFILES_OEM_OVERVIEW;
          break;
        case 'DRACCPR':
          ga2State = STATE_BPPROFILES_VENDOR_ACCREDITATIONS;
          break;
        case 'LOCSERV':
          ga2State = STATE_BPPROFILES_CATALOG;
          break;
        case 'Docs':
          ga2State = STATE_BPPROFILES_DOCUMENTS;
          break;
        case 'ACSLInfo':
          ga2State = STATE_BPPROFILES_ACSLINFO;
          break;
        case 'Mails':
          ga2State = STATE_BPPROFILES_MAILS;
          break;
        case 'Issues':
          ga2State = STATE_BPPROFILES_TICKETS;
          break;
        case 'Contract':
          ga2State = STATE_BPPROFILES_CONTRACT;
          break;
        case 'DRTERIT':
          ga2State = STATE_BPPROFILES_TERRITORIES;
          break;
        default:
          ga2State = STATE_BPPROFILES_GENERAL;
      }

      this.stateService.go(ga2State, ga2Params, stateRedirectOptions);
    }
  };

  private faqRedirect = (transition:Transition):void => {
    const toState:StateDeclaration = transition.to();
    const url:string = transition.params('to')?.url;
    
    //https://localhost:7071/ga2/#/unacc?url=%2Funacc%2FFAQPageView_Edit.jsp
    if (toState.name === STATE_HOME_PAGE
      && url && url.indexOf(GA1_URL_FAQ) >= 0) {

      console.debug('Go to GA2 FAQ');

      const ga1Params:any = urlUtil.getQueryParams(url);
      let keyword:string = ga1Params.Dekeywo;
      const faqId:string = ga1Params.Defaqid;
      keyword = keyword && keyword.replace('TC', 'T&C'); //GA1 removes '&' from the keyword param

      let ga2State:string, ga2Params:IObject;
      if (faqId) {
        ga2State = STATE_FAQ;
        ga2Params = {
          key: faqId
        };
      } else {
        ga2State = STATE_FAQS;
        ga2Params = {
          title: ga1Params.wv_Detitle,
          autoload: !keyword,
          keyword
        };
      }
      this.stateService.go(ga2State, ga2Params, stateRedirectOptions);
    }
  };

  private webshopInfoRedirect = (transition:Transition):void => {
    const toState:StateDeclaration = transition.to();
    const url:string = transition.params('to')?.url;
    
    // Redirect if ga1 url is using template of Webshop Setup, Master Account Info, and Master Webshop Info
    // Or if ga1 url is webshop wizard
    if (toState.name === STATE_HOME_PAGE
      && ((url?.indexOf(GA1_URL_WEBSHOPINFO) >= 0 && (url.indexOf('wv_Template=WPN_SetUp.htm') >= 0 || url.indexOf('wv_Template=WPN_Account.htm') >= 0 || url.indexOf('wv_Template=WPN_Info.htm') >= 0))
        || url?.indexOf(GA1_URL_WEBSHOPWIZARD) >= 0)) {

      console.debug('Go to GA2 Webshop Info');

      const ga1Params:any = urlUtil.getQueryParams(url);
      const webshopId:string = ga1Params.row0_Desprid || ga1Params.Desprid;

      let ga2State:string, ga2Params:IObject;
      if (webshopId) {
        ga2State = STATE_WEBSHOPINFO;
        ga2Params = {
          webshopId,
          menu: this.ga1MenuService.getMenuParamValue(webshopId)
        };
      } else {
        ga2State = STATE_WEBSHOPSETUP; // create new webshop
        ga2Params = {
          webshopId: 'NEW'
        };
      }
      this.stateService.go(ga2State, ga2Params, stateRedirectOptions);
    }
  };

  public isGA1LoginState(name:string, params:StateParams):boolean {
    const currentGA1Url:string = name === STATE_HOME_PAGE && params.url;
    const ga1LoginState:boolean = currentGA1Url && (currentGA1Url.indexOf(GA1_URL_LOGIN) >= 0 || currentGA1Url.indexOf(GA1_URL_RENEW) >= 0);
    return ga1LoginState;
  }

}